// Shared property-class label mapping.
//
// The backend stores `properties.property_class` as a snake_case enum
// (`land_only`, `residential`, `commercial`, etc.). The frontend MUST
// translate to a human-readable label before rendering — surfacing the
// raw key in UI is unprofessional and surfaces in user-facing audits
// (#184: "land_only: 7" appeared as a pie-chart slice label).
//
// Two consumers today: PropertyDetail (also tracks per-class color for
// pill styling) and PortfolioView (uses the label-only map for chart
// slice labels). Keep this file small and side-effect-free.

/** Human-readable display label for each known property_class value. */
export const PROPERTY_CLASS_LABELS: Record<string, string> = {
  land_only: "Land Only",
  residential: "Residential",
  commercial: "Commercial",
  multi_family: "Multi-Family",
  industrial: "Industrial",
  agricultural: "Agricultural",
  unknown: "Unknown",
};

/**
 * Convert a raw property_class enum to a display label.
 * Falls back to a Title-Cased version of the underscored key if unknown,
 * so a new backend enum value still renders cleanly (e.g. "mobile_home"
 * → "Mobile Home") instead of leaking the raw key to users.
 */
export function propertyClassLabel(raw: string | null | undefined): string {
  if (!raw) return PROPERTY_CLASS_LABELS.unknown;
  if (PROPERTY_CLASS_LABELS[raw]) return PROPERTY_CLASS_LABELS[raw];
  // Fallback: Title Case the snake_case key.
  return raw
    .split("_")
    .map((part) => (part ? part.charAt(0).toUpperCase() + part.slice(1) : ""))
    .join(" ")
    .trim() || raw;
}
